import React, { useEffect, memo, useState, useCallback, useRef } from 'react'
import { observer } from 'mobx-react'
import { NavigateFunction } from 'react-router'
import { Typography, Button } from '@mui/material'
import {
  RegisteredEventName,
  broadcastToParent
} from '@finanzcheck/catalyst-pollard'
import { getButtonSizes } from 'components/FormNavigation/getButtonSizes'
import { useSaveButton } from 'hooks/useSaveButton'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import * as CSSTypes from 'csstype'
import useEmotionStyles from 'hooks/useEmotionStyles'
import LegalHintTexts from 'texts/common/copy/legal'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import {
  IBAN_PATH,
  usePayoutAccountValidation
} from 'hooks/usePayoutAccountValidation'
import {
  CONFIG_DDF_CAT_4149_VAR_D,
  CONFIG_DDF_SMAVA,
  CONFIG_SECOND_DEBTOR_SMAVA,
  isRceEditConfig
} from 'config/utils/config'
import { useField, useFormState } from 'react-final-form'
import FormNavigationTemplate from 'components/FormNavigation/FormNavigationTemplate'
import { getAge } from 'utils/date'
import { AgeGroup } from 'constants/components'
import { isPremiumCustomer } from 'utils/user'
import { getAgeGroupFromAge } from 'stores/helpers/AgeGroups'
import LegalHint from 'components/LegalHint'
import ProgressiveDisclosed from 'components/ProgressiveDisclosed'
import CategoryLayout from 'components/Layout/Category'
import { progressiveDisclosureValidator, VALIDATOR_EMAIL } from 'validation'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FakeOffers from 'components/AbTestComponents/CAT4149/FakeOffers'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { Experiments } from 'CustomOptimizelyProvider'

type Props = {
  onBack?: () => void
  onNext: () => void
  legalContact: string
  navigate: NavigateFunction
  isOfferLabel?: boolean
}

const styles = () => ({
  flexBasisGrid: {
    flexBasis: 'auto',
    marginTop: '5px'
  },
  button: {
    maxHeight: '48px'
  },
  buttonFullHeight: {
    height: '48px',
    '& button': {
      height: '48px'
    }
  },
  buttonTitle: {
    color: '#fff',
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: '1.5rem'
  },
  buttonHint: {
    color: '#fff',
    fontSize: '0.75rem',
    lineHeight: '1.25rem'
  },
  buttonTextContainer: {
    flexDirection: 'column' as CSSTypes.Property.FlexDirection,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
})

const FormNavigation = observer(
  ({ onBack, onNext, isOfferLabel, navigate }: Props) => {
    const {
      rootStore: {
        setIsSkipDacCTAVisible,
        isCAT4291DacShowOnCtaClick,
        experimentData,
        isInIframe,
        urlParams: { backUrl },
        traversal: {
          data: {
            common: { amount },
            debtors: {
              primary: {
                personal: { birthday },
                income
              }
            }
          }
        }
      }
    } = useRootStore()

    const isPayoutAccountReady = usePayoutAccountValidation(true)
    const [
      isIbanPresentOnPayoutAccountPage,
      setIsIbanPresentOnPayoutAccountPage
    ] = useState(false)

    const [showExperimentDacSkipButton, setShowExperimentDacSkipButton] =
      useState(false)

    const [skipButtonText, setSkipButtonText] = useState('')

    const [skipButtonShownReason, setSkipButtonShownReason] = useState('')
    const {
      page: {
        isFirstDisplayedPage,
        getCurrentIndex,
        isLastDisplayedPage,
        config
      },
      navigation: { currentPageInSession },
      traversal: { data, dacWidgetState }
    } = useStores()
    const { values } = useFormState()

    const isLastPage = isLastDisplayedPage(currentPageInSession)
    const isDacPage = currentPageInSession === 'dac'
    const isUserInExperiment =
      // @ts-ignore - experimentData type needs refinement for dynamic access
      experimentData[Experiments.CAT_4291_DAC_SKIP_BUTTON_V3] ===
      PossibleVariants.VARIATION_B

    const ibanField = useField(IBAN_PATH, {})

    const { isSaveButtonVisible } = useSaveButton({
      onSuccess: () => {},
      onError: () => {}
    })

    const debtorsAge = getAge(birthday)
    const ageGroup = getAgeGroupFromAge(debtorsAge)

    const isYoungOrMiddleAgeGroup =
      ageGroup === AgeGroup.YOUNG_AGE_GROUP ||
      ageGroup === AgeGroup.MIDDLE_AGE_GROUP

    const isMiddleAgeGroup = ageGroup === AgeGroup.MIDDLE_AGE_GROUP
    const isInRceRoute = isRceEditConfig(config.name)
    const isInRceDacPage = isInRceRoute && isDacPage

    const isPremium =
      isPremiumCustomer({ debtorsAge, amount, income }) ||
      ageGroup === AgeGroup.OLD_AGE_GROUP
    const isUserInExperimentRef = useRef(isUserInExperiment)

    useEffect(() => {
      isUserInExperimentRef.current = isUserInExperiment
    }, [isUserInExperiment])

    const showSkipDacButton =
      (isMiddleAgeGroup &&
        isDacPage &&
        !isPremium &&
        !isUserInExperiment &&
        !isInRceRoute &&
        config.name !== CONFIG_SECOND_DEBTOR_SMAVA) ||
      (isInRceRoute && isDacPage)

    useEffect(() => {
      setShowExperimentDacSkipButton(isCAT4291DacShowOnCtaClick)
      setSkipButtonShownReason('dac_widget_loaded_with_show_on_cta_click')
    }, [isCAT4291DacShowOnCtaClick])

    useEffect(() => {
      if (isUserInExperiment) {
        setSkipButtonText(
          isPremium
            ? 'Weiter ohne digitalen Kontoauszug'
            : 'Ich habe keinen Online-Banking-Zugang'
        )
        return
      }

      setSkipButtonText(
        isInRceRoute || isMiddleAgeGroup
          ? 'Ich habe keinen Online-Banking-Zugang'
          : 'Kontoauszüge manuell hochladen'
      )
    }, [isInRceRoute, isMiddleAgeGroup, isUserInExperiment, isPremium])

    useEffect(() => {
      if (!isDacPage) return
      const timeOut = isUserInExperiment
        ? isPremium
          ? 15000
          : ageGroup === AgeGroup.YOUNG_AGE_GROUP
            ? 60000
            : 20000
        : 0
      const timeoutId = window.setTimeout(() => {
        setSkipButtonShownReason('timer')
        setShowExperimentDacSkipButton(isUserInExperimentRef.current)
      }, timeOut)

      return () => {
        window.clearTimeout(timeoutId)
      }
    }, [isUserInExperiment, isPremium, isDacPage, ageGroup])

    useEffect(() => {
      const onTabVisibilityChange = () => {
        if (document.visibilityState !== 'hidden') {
          return
        }
        if (!isUserInExperiment || !isDacPage || !isYoungOrMiddleAgeGroup) {
          return
        }
        setSkipButtonShownReason('left_tab')
        setShowExperimentDacSkipButton(true)
        setSkipButtonText('Weiter ohne digitalen Kontoauszug')
      }
      document.addEventListener('visibilitychange', onTabVisibilityChange)
      return () => {
        document.removeEventListener('visibilitychange', onTabVisibilityChange)
      }
    }, [isUserInExperiment, isDacPage, isYoungOrMiddleAgeGroup])

    useEffect(() => {
      setIsIbanPresentOnPayoutAccountPage(
        currentPageInSession === 'payoutAccount' &&
          ibanField.input.value &&
          dacWidgetState === 'initial'
      )
    }, [data, currentPageInSession, dacWidgetState])

    const currentIndex = getCurrentIndex()

    const trackWithOrigin = (event: AmplitudeEvent, payload = {}) => {
      trackInAmplitude(event, { origin: 'taurine', ...payload })
    }

    const onNextWithTracking = useCallback(() => {
      onNext()
    }, [onNext, data])

    useEffect(() => {
      if (dacWidgetState === 'error' && isUserInExperiment && !isPremium) {
        setShowExperimentDacSkipButton(true)
        setSkipButtonShownReason('error_in_dac')
        setSkipButtonText('Weiter ohne digitalen Kontoauszug')
      }
      if (dacWidgetState === 'success') {
        trackWithOrigin(AmplitudeEvent.OnDACSuccessAutoRedirect)
        onNextWithTracking()
      }
    }, [dacWidgetState, isUserInExperiment, isPremium])

    useEffect(() => {
      if (showExperimentDacSkipButton) {
        setIsSkipDacCTAVisible(true)
        broadcastToParent({
          eventName: RegisteredEventName.tracking,
          data: {
            event: 'skip_button_shown',
            context: {
              Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
              ...getOptimizelyTrackingAttribute(data)
            },
            payload: {
              shown_reason: skipButtonShownReason,
              text_on_button: skipButtonText
            }
          }
        })
      }
    }, [showExperimentDacSkipButton, skipButtonText, skipButtonShownReason])

    const skipDacButtonOnClick = useCallback(() => {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'skip_dac_button_triggered',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'skip_button_clicked',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          },
          payload: {
            text_on_button: skipButtonText
          }
        }
      })
      onNext()
    }, [onNext])

    const { buttonTitle, buttonHint, buttonTextContainer } =
      useEmotionStyles(styles)

    const isFirstPage = isFirstDisplayedPage(currentPageInSession)

    const getSubmitLabel = () => {
      if (isFirstPage || isOfferLabel) {
        const title =
          isOfferLabel || ageGroup === AgeGroup.OLD_AGE_GROUP
            ? 'Weiter zu den Angeboten'
            : 'Kreditvergleich starten'

        const hint = 'kostenlos und unverbindlich'

        return (
          <div className={buttonTextContainer}>
            <Typography className={buttonTitle}>{title}</Typography>
            <Typography className={buttonHint}>{hint}</Typography>
          </div>
        )
      }

      return 'Weiter'
    }

    const isContinueWithoutDacVisible =
      isRceEditConfig(config.name || '') &&
      isLastPage &&
      dacWidgetState !== 'success' &&
      !isInRceRoute

    const backLabel = !isInIframe || currentIndex > 0 || backUrl ? 'Zurück' : ''

    const isBackButtonHidden = !backLabel || isFirstPage

    const getIsContinueButtonHidden = () => {
      if (isRceEditConfig(config.name || '')) {
        return isLastPage && dacWidgetState !== 'success'
      }

      if (isLastPage && isPremium) {
        return true
      }

      if (config.name === CONFIG_SECOND_DEBTOR_SMAVA) {
        return false
      }

      return (
        isLastPage &&
        isPayoutAccountReady &&
        dacWidgetState !== 'success' &&
        !isIbanPresentOnPayoutAccountPage &&
        isDacPage &&
        ageGroup !== AgeGroup.OLD_AGE_GROUP
      )
    }

    const showDacLegalText =
      isLastPage &&
      isPayoutAccountReady &&
      isDacPage &&
      config.name === CONFIG_DDF_SMAVA

    const isContinueButtonHidden = getIsContinueButtonHidden()

    const { sizeOfContinueButton, sizeOfBackButton, sizeOfSaveButton } =
      getButtonSizes(
        isBackButtonHidden,
        isSaveButtonVisible,
        isContinueButtonHidden
      )

    const onBackHandler = () => {
      if (isLastPage) {
        broadcastToParent({
          eventName: RegisteredEventName.tracking,
          data: {
            event: 'back_button_click_on_dac_page',
            context: {
              Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
              ...getOptimizelyTrackingAttribute(data)
            }
          }
        })
      }
      if (
        dacWidgetState !== 'initial' &&
        isLastPage &&
        !isMiddleAgeGroup &&
        !isInRceRoute &&
        !isUserInExperiment
      ) {
        broadcastToParent({
          eventName: RegisteredEventName.tracking,
          data: {
            event: 'final_on_back_button_click',
            context: {
              Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
              ...getOptimizelyTrackingAttribute(data)
            }
          }
        })
        onNext()
        return
      }
      onBack && onBack()
    }

    const continueWithoutDacButtonVariant = isRceEditConfig(config.name || '')
      ? 'outlined'
      : 'text'

    if (
      currentPageInSession === 'contact' &&
      config.name === CONFIG_DDF_CAT_4149_VAR_D
    ) {
      return (
        <CategoryLayout>
          <Button
            size="large"
            fullWidth
            color="primary"
            variant="contained"
            onClick={onNext}
            endIcon={<ArrowForwardIcon />}
            sx={{
              height: '48px',
              fontSize: 16
            }}
          >
            {'Weiter'}
          </Button>
          <ProgressiveDisclosed
            value={values.debtors?.primary?.contact?.email}
            validator={progressiveDisclosureValidator(VALIDATOR_EMAIL)}
            testId="agb-smava"
          >
            <LegalHint legalText={LegalHintTexts.smavaLegalHint} />
          </ProgressiveDisclosed>
          <FakeOffers
            fakeBankListNames={['postbank', 'ingDiba', 'commerzbank']}
          />
        </CategoryLayout>
      )
    }

    return (
      <FormNavigationTemplate
        onBack={onBackHandler}
        onNext={onNext}
        navigate={navigate}
        legalText={LegalHintTexts.smavaLegalHint}
        isContinueWithoutDacVisible={isContinueWithoutDacVisible}
        isBackButtonHidden={isBackButtonHidden}
        isContinueButtonHidden={isContinueButtonHidden}
        sizeOfContinueButton={sizeOfContinueButton}
        sizeOfBackButton={isInRceDacPage ? 4 : sizeOfBackButton}
        sizeOfSaveButton={
          isDacPage && (showSkipDacButton || showExperimentDacSkipButton)
            ? 4
            : sizeOfSaveButton
        }
        submitLabel={getSubmitLabel()}
        backLabel={backLabel}
        sizeOfSkipDacButton={8}
        onNextWithTracking={onNextWithTracking}
        trackWithOrigin={trackWithOrigin}
        continueWithoutDacButtonVariant={continueWithoutDacButtonVariant}
        skipDacButton={
          isDacPage && (showSkipDacButton || showExperimentDacSkipButton) ? (
            <Button
              id="test-skip-dac"
              name="test-skip-dac"
              onClick={skipDacButtonOnClick}
              color="primary"
              fullWidth
              variant={'outlined'}
              endIcon={isUserInExperiment ? <ArrowForwardIcon /> : undefined}
              sx={{
                fontWeight: 500,
                fontSize: '15px',
                height: '50px'
              }}
            >
              {skipButtonText}
            </Button>
          ) : null
        }
        showDacLegalText={showDacLegalText}
      />
    )
  }
)

export default memo(FormNavigation)
